import * as logger from 'loglevel';
import { getUuidMin } from '@front10/helpers/dist/uuid';
import { getDateRangeAsDates } from '../../helpers/localization/localizationUtils';
import transformRequestGroupRoutes from './transformRequest/transformGroupRoutes';
import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };
  if (!fares || !Array.isArray(fares)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: Fares must be an Array'
    };
  }
  if (fares.length > 0) {
    const fareItem = fares[0];
    if (!fareItem.hotel || !fareItem.journey || !fareItem.priceSpecification) {
      return {
        isValid: false,
        data: fareItem,
        message:
          'INTEGRATION__FARES: FareItem SCHEMA is invalid, please check Fares API documentation.'
      };
    }
  }
  return validation;
};

const getCorrectImage = (hotelRaw, journeyRaw) => {
  if (hotelRaw.images?.length > 0 && hotelRaw.images[0]) {
    if (hotelRaw.images[0].includes('https://') || hotelRaw.images[0].includes('http://')) {
      return hotelRaw.images[0];
    } else {
      return hotelRaw.images[0].replace('//', 'https://');
    }
  } else {
    return (
      journeyRaw.outboundFlight.destination?.city?.image ||
      journeyRaw.outboundFlight.destination?.country?.image
    );
  }
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} routesRawData
 * @param {*} query
 */
const transformData = (routesRawData) => {
  const fares = routesRawData
    .map(
      ({
        hotel: hotelRaw,
        journey: journeyRaw,
        priceSpecification: priceSpecificationRaw,
        searchDate
      }) => {
        // Workaround for Safari and IE
        if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
          searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
            searchDate.length - 2
          )}`;
        }

        let fields = journeyRaw.additionalInfo?.fields;
        const originDestination = Boolean(fields)
          ? fields.filter((field) => field.name === 'origin' || field.name === 'destination')
          : [];

        //PENDING: Las prioridades de las imágenes.
        const newFare = {
          hotelId: hotelRaw.id,
          hotelGroup: hotelRaw.group,
          hotelName: hotelRaw.name,
          hotelRoomCount: hotelRaw.roomCount,
          rating: hotelRaw?.rating?.value,
          ...journeyRaw,
          origin: journeyRaw.outboundFlight.departureAirportIataCode,
          aditionalOrigin: originDestination.length > 0 ? originDestination[0].value : '',
          originCity: journeyRaw.outboundFlight.origin?.city?.name,
          destination: journeyRaw.outboundFlight.arrivalAirportIataCode,
          aditionalDestination: originDestination.length > 1 ? originDestination[1].value : '',
          destinationCity: journeyRaw.outboundFlight.destination?.city?.name,
          farenetTravelClass: journeyRaw.outboundFlight.fareClass,
          ...priceSpecificationRaw,
          // journeyType: journeyTypeMap[flightType],
          searchDate,
          destinationImage: getCorrectImage(hotelRaw, journeyRaw)
        };

        newFare.index = getUuidMin(
          `${newFare.hotelId}${newFare.hotelGroup}
            ${newFare.hotelName}
            ${newFare.hotelRoomCount}
            ${newFare.rating}
            ${newFare.origin}
            ${newFare.destination}
            ${newFare.farenetTravelClass}
            ${searchDate}`
        );

        delete newFare.outboundFlight;
        return newFare;
      }
    )
    .reduce((a, b) => a.concat(b), []); // arr.flat()
  return fares;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFares = (filter, integrationSettings, withAgreggations, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const {
      v1Globetrotter: faresApiSettings,
      v1Globetrotter: { cache = false, cacheDuration = NO_DURATION_DEFINED }
    } = integrationSettings;
    const filterCopy = { ...filter };

    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.v1Globetrotter** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__FARES'
    );
    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__FARES'
      );
    }

    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}`,
        headers: { ...faresApiSettings.headers },
        data: transformRequestGroupRoutes(filterCopy)
      },
      {
        resource: 'INTEGRATION__FARES__GLOBETROTTER',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateFaresResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', response.data);
          const fares = transformData(response.data);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFares, transformData };
